import { useAuth, useAuthStore } from "../hooks/useAuth";
import { Navigate } from "react-router-dom";
import axios from "../lib/axios";
import { useState, useEffect, type Dispatch, type SetStateAction } from "react";
import { Copy, Check } from "lucide-react";
import GdprTab from "../components/GdprTab";

interface Tenant {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  subdomain?: string;
  status: string;
  createdAt: string;
  _count: { users: number; events: number };
}

interface TenantWithUsers extends Tenant {
  expanded?: boolean;
  users?: any[];
  loadingUsers?: boolean;
}

interface TenantFormState {
  name: string;
  slug: string;
  domain: string;
  subdomain: string;
  adminEmail: string;
  adminNome: string;
}

interface TenantTabProps {
  tenants: TenantWithUsers[];
  setTenants: Dispatch<SetStateAction<TenantWithUsers[]>>;
  loading: boolean;
  error: string;
  newTenant: TenantFormState;
  setNewTenant: Dispatch<SetStateAction<TenantFormState>>;
  creatingTenant: boolean;
  createTenant: () => Promise<void>;
}

type TabType = "staff" | "settings" | "account" | "tenants" | "tenant-staff" | "gdpr";

export default function SuperAdminPage() {
  const { user, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>("staff");
  const [tenants, setTenants] = useState<TenantWithUsers[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newTenant, setNewTenant] = useState<TenantFormState>({
    name: "",
    slug: "",
    domain: "",
    subdomain: "",
    adminEmail: "",
    adminNome: "",
  });
  const [creatingTenant, setCreatingTenant] = useState(false);

  // Protect: only superadmin (tenantId=null, role=superadmin)
  if (!isLoading && (!user || user?.tenantId !== null || user?.role !== "superadmin")) {
    return <Navigate to="/login" replace />;
  }

  useEffect(() => {
    if (!isLoading && user && user.tenantId === null && user.role === "superadmin") {
      fetchTenants();
    }
  }, [isLoading, user]);

  const fetchTenants = async () => {
    try {
      const res = await axios.get("/platform/tenants");
      setTenants(res.data.data || []);
      setError("");
    } catch (err) {
      setError("Failed to load tenants");
    } finally {
      setLoading(false);
    }
  };

  const createTenant = async () => {
    if (!newTenant.name || !newTenant.slug) {
      alert("Name and slug required");
      return;
    }

    setCreatingTenant(true);
    try {
      const res = await axios.post("/platform/tenants", {
        name: newTenant.name,
        slug: newTenant.slug,
        domain: newTenant.domain || undefined,
        subdomain: newTenant.subdomain || undefined,
        adminEmail: newTenant.adminEmail,
        adminNome: newTenant.adminNome,
      });
      setTenants([res.data.data.tenant, ...tenants]);
      setNewTenant({ name: "", slug: "", domain: "", subdomain: "", adminEmail: "", adminNome: "" });
      alert("Tenant created successfully. Admin invitation email sent.");
    } catch (err: any) {
      alert(err.response?.data?.error?.message || "Failed to create tenant");
    } finally {
      setCreatingTenant(false);
    }
  };


  return (
    <div className="font-sans space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 via-orange-400 to-orange-500 rounded-xl p-8 text-white shadow-lg">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2">Platform Administration</h1>
            <p className="text-orange-50 text-lg">Manage tenants, staff, settings and your account</p>
          </div>
          <div className="text-6xl opacity-30">🛡️</div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex gap-2 border-b-2 border-orange-100 bg-orange-50 rounded-t-xl px-2 py-1 overflow-x-auto">
        {[
          { id: "staff" as TabType, label: "Staff", icon: "👥" },
          { id: "tenants" as TabType, label: "Tenants", icon: "🏢" },
          { id: "tenant-staff" as TabType, label: "Tenant Staff", icon: "👔" },
          { id: "gdpr" as TabType, label: "GDPR", icon: "📋" },
          { id: "settings" as TabType, label: "Settings", icon: "⚙️" },
          { id: "account" as TabType, label: "Account", icon: "👤" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 rounded-t-lg font-semibold text-sm transition-all duration-200 whitespace-nowrap flex items-center gap-2 ${
              activeTab === tab.id
                ? "bg-white text-orange-500 border-b-2 border-orange-500 shadow-md -mb-0.5"
                : "bg-orange-50 text-gray-600 border-b-2 border-transparent hover:bg-orange-100 hover:text-black"
            }`}
          >
            <span className="text-base">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="px-2 bg-white rounded-b-xl shadow-lg border border-[#E8C8B8]">
        {activeTab === "staff" && <StaffTab />}
        {activeTab === "settings" && <SettingsTab />}
        {activeTab === "account" && <AccountTab user={user} />}
        {activeTab === "tenants" && <TenantTab tenants={tenants} setTenants={setTenants} loading={loading} error={error} newTenant={newTenant} setNewTenant={setNewTenant} creatingTenant={creatingTenant} createTenant={createTenant} />}
        {activeTab === "tenant-staff" && <TenantStaffTab />}
        {activeTab === "gdpr" && <GdprTab isSuperAdmin={true} />}
      </div>
    </div>
  );
}

// ============ TAB: TENANT ============
function TenantTab({ tenants, setTenants, loading, error, newTenant, setNewTenant, creatingTenant, createTenant }: TenantTabProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState({ name: "", slug: "", domain: "", subdomain: "" });
  const [editError, setEditError] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const startEdit = (tenant: any) => {
    setEditingId(tenant.id);
    setEditData({ name: tenant.name, slug: tenant.slug, domain: tenant.domain || "", subdomain: tenant.subdomain || "" });
    setEditError("");
    setEditMessage("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({ name: "", slug: "", domain: "", subdomain: "" });
    setEditError("");
  };

  const handleUpdate = async (tenantId: string) => {
    if (!editData.name.trim() || !editData.slug.trim()) {
      setEditError("Name and slug are required");
      return;
    }

    setUpdatingId(tenantId);
    setEditError("");
    setEditMessage("");

    try {
      const res = await axios.put(`/platform/tenants/${tenantId}`, {
        name: editData.name.trim(),
        slug: editData.slug.trim(),
        domain: editData.domain?.trim() || null,
        subdomain: editData.subdomain?.trim() || null,
      });

      if (res.data.success) {
        const updatedTenant = res.data.data?.tenant || res.data.data;
        if (updatedTenant) {
          setTenants((current: TenantWithUsers[]) =>
            current.map((tenant: TenantWithUsers) =>
              tenant.id === tenantId ? { ...tenant, ...updatedTenant } : tenant,
            ),
          );
        }
        setEditMessage("Tenant updated successfully");
        setEditingId(null);
      }
    } catch (err: any) {
      setEditError(err.response?.data?.error?.message || "Failed to update tenant");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSuspend = async (tenantId: string) => {
    if (!confirm("Suspend this tenant? Users will not be able to access it.")) return;

    try {
      const res = await axios.patch(`/platform/tenants/${tenantId}/status`, {
        status: "suspended",
      });

      if (res.data.success) {
        const updatedTenant = res.data.data?.tenant || res.data.data;
        setTenants((current: TenantWithUsers[]) =>
          current.map((tenant: TenantWithUsers) =>
            tenant.id === tenantId
              ? { ...tenant, ...(updatedTenant || { status: "suspended" }) }
              : tenant,
          ),
        );
        alert("Tenant suspended successfully");
      }
    } catch (err: any) {
      alert(err.response?.data?.error?.message || "Failed to suspend tenant");
    }
  };

  const handleResume = async (tenantId: string) => {
    if (!confirm("Resume this tenant? Users will be able to access it again.")) return;

    try {
      const res = await axios.patch(`/platform/tenants/${tenantId}/status`, {
        status: "active",
      });

      if (res.data.success) {
        const updatedTenant = res.data.data?.tenant || res.data.data;
        setTenants((current: TenantWithUsers[]) =>
          current.map((tenant: TenantWithUsers) =>
            tenant.id === tenantId
              ? { ...tenant, ...(updatedTenant || { status: "active" }) }
              : tenant,
          ),
        );
        alert("Tenant resumed successfully");
      }
    } catch (err: any) {
      alert(err.response?.data?.error?.message || "Failed to resume tenant");
    }
  };

  const [copiedTenantId, setCopiedTenantId] = useState<string | null>(null);

  const generateRegistrationLink = (tenant: any) => {
    if (!tenant.domain || !tenant.id) return "";
    return `https://${tenant.domain}/visitor-registration?tenantId=${tenant.id}&source=nfc`;
  };

  const copyRegistrationLink = (tenant: any) => {
    const link = generateRegistrationLink(tenant);
    if (!link) return;
    navigator.clipboard.writeText(link);
    setCopiedTenantId(tenant.id);
    setTimeout(() => setCopiedTenantId(null), 2000);
  };

  const handleDelete = async (tenantId: string) => {
    if (!confirm("Delete this tenant? This action cannot be undone.")) return;

    try {
      const res = await axios.delete(`/platform/tenants/${tenantId}`);

      if (res.data.success) {
        setTenants((current: TenantWithUsers[]) => current.filter((tenant: TenantWithUsers) => tenant.id !== tenantId));
        alert("Tenant deleted successfully");
      }
    } catch (err: any) {
      alert(err.response?.data?.error?.message || "Failed to delete tenant");
    }
  };

  return (
    <div className="space-y-6 p-3">
      {/* Create Tenant */}
      <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md hover:shadow-lg transition-shadow">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1F1411] mb-1">Create New Tenant</h2>
            <p className="text-[#8B7355] text-base">Set up a new tenant instance with dedicated admin user</p>
          </div>
          <div className="text-4xl">🏢</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-[#1F1411] mb-2">Tenant Name</label>
            <input
              type="text"
              placeholder="e.g. Museum of Rome"
              value={newTenant.name}
              onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#1F1411] mb-2">Slug</label>
            <input
              type="text"
              placeholder="e.g. rome-tourism"
              value={newTenant.slug}
              onChange={(e) => setNewTenant({ ...newTenant, slug: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#1F1411] mb-2">Domain (Optional)</label>
            <input
              type="text"
              placeholder="e.g. rome.sati.local"
              value={newTenant.domain}
              onChange={(e) => setNewTenant({ ...newTenant, domain: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#1F1411] mb-2">Subdomain (Optional)</label>
            <input
              type="text"
              placeholder="e.g. rome"
              value={newTenant.subdomain}
              onChange={(e) => setNewTenant({ ...newTenant, subdomain: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
            />
          </div>
          <button
            onClick={createTenant}
            disabled={creatingTenant}
            className={`px-6 py-3 rounded-lg font-semibold text-base whitespace-nowrap text-white transition-all duration-200 self-end h-12 ${
              creatingTenant
                ? "bg-[#C0996F] cursor-not-allowed"
                : "bg-gradient-to-r from-[#D4A574] to-[#E5923A] hover:from-[#E5923A] hover:to-[#D4A574] hover:shadow-lg active:scale-95"
            }`}
          >
            {creatingTenant ? "Creating..." : "Create Tenant"}
          </button>
        </div>
      </div>

      {/* Tenants List */}
      <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md">
        <h2 className="text-2xl font-bold text-[#1F1411] mb-6">All Tenants ({tenants.length})</h2>

        {loading && <p className="text-[#8B7355] text-lg">Loading...</p>}
        {error && <p className="text-[#E5923A] font-medium">{error}</p>}

        {!loading && tenants.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-[#1F1411] border-collapse">
              <thead>
                <tr className="border-b-2 border-[#E8C8B8] bg-[#FAF8F5]">
                  <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Name</th>
                  <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Slug</th>
                  <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Domain</th>
                  <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">NFC Link</th>
                  <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Subdomain</th>
                  <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Status</th>
                  <th className="text-center py-4 px-4 font-semibold text-[#1F1411]">Users</th>
                  <th className="text-center py-4 px-4 font-semibold text-[#1F1411]">Events</th>
                  <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Created</th>
                  <th className="text-center py-4 px-4 font-semibold text-[#1F1411]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t: any) => (
                  <tr key={t.id} className={`border-b transition-colors ${editingId === t.id ? "bg-[#FFF5E6]" : "hover:bg-[#FAF8F5]"}`}>
                    {editingId === t.id ? (
                      <>
                        <td className="py-4 px-4">
                          <input
                            type="text"
                            value={editData.name}
                            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                            className="w-full px-2 py-1 border border-[#E8C8B8] rounded text-sm"
                          />
                        </td>
                        <td className="py-4 px-4">
                          <input
                            type="text"
                            value={editData.slug}
                            onChange={(e) => setEditData({ ...editData, slug: e.target.value })}
                            className="w-full px-2 py-1 border border-[#E8C8B8] rounded text-sm"
                          />
                        </td>
                        <td colSpan={2}className="py-4 px-4">
                          <input
                            type="text"
                            value={editData.domain}
                            onChange={(e) => setEditData({ ...editData, domain: e.target.value })}
                            className="w-full px-2 py-1 border border-[#E8C8B8] rounded text-sm"
                          />
                        </td>
                        
                        <td className="py-4 px-4">
                          <input
                            type="text"
                            value={editData.subdomain}
                            onChange={(e) => setEditData({ ...editData, subdomain: e.target.value })}
                            className="w-full px-2 py-1 border border-[#E8C8B8] rounded text-sm"
                            placeholder="Subdomain"
                          />
                        </td>
                        <td colSpan={3} className="py-4 px-4 text-[#8B7355]"></td>
                        <td colSpan={4} className="py-4 px-4">
                          {editError && <p className="text-red-600 text-xs mb-2">✕ {editError}</p>}
                          {editMessage && <p className="text-green-600 text-xs mb-2">✓ {editMessage}</p>}
                          
                          <div className="flex gap-2">
                            
                            <button
                              onClick={() => handleUpdate(t.id)}
                              disabled={updatingId === t.id}
                              className="px-3 py-1 bg-green-500 text-white rounded text-xs font-semibold hover:bg-green-600"
                            >
                              Save
                            </button>
                          
                        
                            <button
                              onClick={cancelEdit}
                              className="px-3 py-1 bg-gray-400 text-white rounded text-xs font-semibold hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-4 px-4 font-semibold">{t.name}</td>
                        <td className="py-4 px-4 text-[#8B7355]">{t.slug}</td>
                        <td className="py-4 px-4 text-[#8B7355]">{t.domain || "—"}</td>
                        <td className="py-4 px-4 text-[#8B7355]"><div className="flex items-center gap-2">
                            
                            {t.domain && (
                              <button
                                onClick={() => copyRegistrationLink(t)}
                                title="Copy visitor registration link"
                                className={`p-1 rounded transition-all ${
                                  copiedTenantId === t.id
                                    ? 'bg-green-100 text-green-600'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                                }`}
                              >
                                {copiedTenantId === t.id ? (
                                  <Check size={16} />
                                ) : (
                                  <Copy size={16} />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-[#8B7355]">{t.subdomain || "—"}</td>
                        
                        <td className="py-4 px-4">
                          <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-bold ${
                            t.status === "active"
                              ? "bg-[#D4A574] text-white"
                              : t.status === "suspended"
                              ? "bg-yellow-500 text-white"
                              : "bg-[#E8C8B8] text-[#1F1411]"
                          }`}>
                            {t.status}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center font-medium">{t._count?.users || 0}</td>
                        <td className="py-4 px-4 text-center font-medium">{t._count?.events || 0}</td>
                        <td className="py-4 px-4 text-[#8B7355] text-sm">
                          {new Date(t.createdAt).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex gap-2 justify-center flex-wrap">
                            <button
                              onClick={() => startEdit(t)}
                              className="px-3 py-1 rounded border border-[#D4A574] bg-[#FFF5E6] hover:bg-[#FFEFD5] text-[#D4A574] text-xs font-semibold cursor-pointer transition-all"
                            >
                              Edit
                            </button>
                            {t.status === "active" ? (
                              <button
                                onClick={() => handleSuspend(t.id)}
                                className="px-3 py-1 rounded border border-yellow-500 bg-yellow-50 hover:bg-yellow-100 text-yellow-600 text-xs font-semibold cursor-pointer transition-all"
                              >
                                Suspend
                              </button>
                            ) : t.status === "suspended" ? (
                              <button
                                onClick={() => handleResume(t.id)}
                                className="px-3 py-1 rounded border border-green-500 bg-green-50 hover:bg-green-100 text-green-600 text-xs font-semibold cursor-pointer transition-all"
                              >
                                Resume
                              </button>
                            ) : null}
                            {t.status !== "inactive" && (
                              <button
                                onClick={() => handleDelete(t.id)}
                                className="px-3 py-1 rounded border border-red-500 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold cursor-pointer transition-all"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && tenants.length === 0 && (
          <p className="text-[#C0996F]">No tenants found. Create one above.</p>
        )}
      </div>
    </div>
  );
}

// ============ TAB: STAFF ============
function StaffTab() {
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newStaffUser, setNewStaffUser] = useState({ email: "", nome: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/platform/staff");
      setStaff(res.data.data?.staff || []);
    } catch {
      // ignore - table will show empty
    } finally {
      setLoading(false);
    }
  };

  const createAdmin = async () => {
    if (!newStaffUser.email || !newStaffUser.nome) {
      alert("Email and name required");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post("/platform/users/admin", {
        email: newStaffUser.email,
        nome: newStaffUser.nome,
      });
      setStaff([res.data.data.user, ...staff]);
      setNewStaffUser({ email: "", nome: "" });
      alert("Platform admin created successfully. Invitation email sent.");
    } catch (err: any) {
      alert(err.response?.data?.error?.message || "Failed to create platform admin");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Create Platform Admin Staff Section */}
      <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md hover:shadow-lg transition-shadow">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1F1411] mb-1">Create Platform Admin</h2>
            <p className="text-[#8B7355] text-base">Add staff with platform-level privileges to manage tenants and settings</p>
          </div>
          <div className="text-4xl">👥</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-semibold text-[#1F1411] mb-2">Email</label>
            <input
              type="email"
              placeholder="admin@sati.app"
              value={newStaffUser.email}
              onChange={(e) => setNewStaffUser({ ...newStaffUser, email: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1F1411] mb-2">Full Name</label>
            <input
              type="text"
              placeholder="Jane Admin"
              value={newStaffUser.nome}
              onChange={(e) => setNewStaffUser({ ...newStaffUser, nome: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
            />
          </div>

          <button
            onClick={createAdmin}
            disabled={creating}
            className={`px-6 py-3 rounded-lg font-semibold text-base whitespace-nowrap text-white transition-all duration-200 self-end h-12 ${
              creating
                ? "bg-[#C0996F] cursor-not-allowed"
                : "bg-gradient-to-r from-[#E5923A] to-[#D4A574] hover:from-[#D4A574] hover:to-[#E5923A] hover:shadow-lg active:scale-95"
            }`}
          >
            {creating ? "Creating..." : "Add Staff"}
          </button>
        </div>
      </div>

      {/* Platform Staff List */}
      <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md">
        <h2 className="text-2xl font-bold text-[#1F1411] mb-6">Platform Admin Staff</h2>
        {loading ? (
          <p className="text-[#8B7355] text-center py-4">Loading...</p>
        ) : (
          <table className="w-full text-sm text-[#1F1411] border-collapse">
            <thead>
              <tr className="border-b-2 border-[#E8C8B8] bg-[#FAF8F5]">
                <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Email</th>
                <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Name</th>
                <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Role</th>
                <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Status</th>
                <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Created</th>
              </tr>
            </thead>
            <tbody>
              {staff.length === 0 ? (
                <tr className="border-b border-[#E8C8B8]">
                  <td colSpan={5} className="py-4 px-4 text-[#C0996F] text-center">
                    No platform admin staff yet. Create one above to get started.
                  </td>
                </tr>
              ) : (
                staff.map((member) => (
                  <tr key={member.id} className="border-b border-[#E8C8B8] hover:bg-[#FAF8F5] transition-colors">
                    <td className="py-3 px-4">{member.email}</td>
                    <td className="py-3 px-4">{member.name || "—"}</td>
                    <td className="py-3 px-4 capitalize">{member.role}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        member.status === "active"
                          ? "bg-green-100 text-green-700"
                          : member.status === "inactive"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-gray-100 text-gray-600"
                      }`}>
                        {member.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#8B7355]">
                      {new Date(member.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============ TAB: TENANT STAFF ============
function TenantStaffTab() {
  const [tenants, setTenants] = useState<any[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>("");
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newStaff, setNewStaff] = useState({ email: "", nome: "", role: "operatore" });
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>({ email: "", name: "" });
  const [editError, setEditError] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const res = await axios.get("/platform/tenants");
      setTenants(res.data.data || []);
    } catch (err) {
      setError("Failed to load tenants");
    }
  };

  const fetchStaff = async (tenantId: string) => {
    if (!tenantId) {
      setStaff([]);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`/platform/tenants/${tenantId}/staff`);
      setStaff(res.data.data?.staff || []);
      setError("");
    } catch (err) {
      setError("Failed to load staff");
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTenantChange = (tenantId: string) => {
    setSelectedTenant(tenantId);
    setEditingId(null);
    fetchStaff(tenantId);
  };

  const createStaff = async () => {
    if (!newStaff.email || !newStaff.nome) {
      alert("Email and name required");
      return;
    }

    if (!selectedTenant) {
      alert("Select a tenant first");
      return;
    }

    setCreating(true);
    try {
      const endpoint =
        newStaff.role === "admin"
          ? `/platform/users/admin`
          : `/platform/users/operatore`;
      const res = await axios.post(endpoint, {
        email: newStaff.email,
        nome: newStaff.nome,
        tenantId: selectedTenant,
      });

      setStaff([res.data.data.user, ...staff]);
      setNewStaff({ email: "", nome: "", role: "operatore" });
      alert(
        `Tenant ${newStaff.role} created successfully. Invitation email sent.`
      );
    } catch (err: any) {
      alert(err.response?.data?.error?.message || "Failed to create staff");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (staffMember: any) => {
    setEditingId(staffMember.id);
    setEditData({ email: staffMember.email, name: staffMember.name || "" });
    setEditError("");
    setEditMessage("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({ email: "", name: "" });
    setEditError("");
  };

  const handleUpdate = async (staffId: string) => {
    if (!editData.email.trim()) {
      setEditError("Email is required");
      return;
    }

    setUpdatingId(staffId);
    try {
      const res = await axios.put(
        `/platform/tenants/${selectedTenant}/staff/${staffId}`,
        { email: editData.email, name: editData.name }
      );
      setStaff(staff.map((s) => (s.id === staffId ? res.data.data : s)));
      setEditingId(null);
      setEditMessage("Staff updated successfully");
      setTimeout(() => setEditMessage(""), 3000);
    } catch (err: any) {
      setEditError(err.response?.data?.error?.message || "Failed to update staff");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSuspend = async (staffId: string) => {
    if (!confirm("Suspend this staff member? They will not be able to access the system.")) return;

    setUpdatingId(staffId);
    try {
      const res = await axios.patch(
        `/platform/tenants/${selectedTenant}/staff/${staffId}/status`,
        { status: "suspended" }
      );
      setStaff(staff.map((s) => (s.id === staffId ? res.data.data : s)));
      alert("Staff member suspended successfully");
    } catch (err: any) {
      alert(err.response?.data?.error?.message || "Failed to suspend staff member");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleResume = async (staffId: string) => {
    if (!confirm("Resume this staff member? They will be able to access the system again.")) return;

    setUpdatingId(staffId);
    try {
      const res = await axios.patch(
        `/platform/tenants/${selectedTenant}/staff/${staffId}/status`,
        { status: "active" }
      );
      setStaff(staff.map((s) => (s.id === staffId ? res.data.data : s)));
      alert("Staff member resumed successfully");
    } catch (err: any) {
      alert(err.response?.data?.error?.message || "Failed to resume staff member");
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteStaff = async (staffId: string) => {
    if (!confirm("Delete this staff member? This action cannot be undone.")) return;

    try {
      await axios.delete(`/platform/tenants/${selectedTenant}/staff/${staffId}`);
      setStaff(staff.filter((s) => s.id !== staffId));
      alert("Staff member deleted");
    } catch (err) {
      alert("Failed to delete staff member");
    }
  };

  return (
    <div className="space-y-6">
      {/* Tenant Selection */}
      <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md hover:shadow-lg transition-shadow">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-[#1F1411]">Select Tenant</h2>
            <p className="text-[#8B7355] text-sm mt-1">Choose a tenant to manage staff</p>
          </div>
          <div className="text-4xl">🏢</div>
        </div>
        <select
          value={selectedTenant}
          onChange={(e) => handleTenantChange(e.target.value)}
          aria-label="Select a tenant"
          className="w-full md:w-96 px-4 py-3 rounded-lg border border-[#E8C8B8] text-base font-medium focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574] bg-white"
        >
          <option value="">-- Select a tenant --</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.slug})
            </option>
          ))}
        </select>
      </div>

      {selectedTenant && (
        <>
          {/* Create Tenant Staff Section */}
          <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-[#1F1411] mb-1">Add Tenant Staff</h2>
                <p className="text-[#8B7355] text-base">Create admin or operator roles for tenant management</p>
              </div>
              <div className="text-4xl">👤</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-sm font-semibold text-[#1F1411] mb-2">Email</label>
                <input
                  type="email"
                  placeholder="staff@museum.local"
                  value={newStaff.email}
                  onChange={(e) =>
                    setNewStaff({ ...newStaff, email: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1F1411] mb-2">Full Name</label>
                <input
                  type="text"
                  placeholder="John Doe"
                  value={newStaff.nome}
                  onChange={(e) =>
                    setNewStaff({ ...newStaff, nome: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1F1411] mb-2">Role</label>
                <select
                  value={newStaff.role}
                  onChange={(e) =>
                    setNewStaff({ ...newStaff, role: e.target.value })
                  }
                  aria-label="Role"
                  className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base font-medium focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574] bg-white"
                >
                  <option value="operatore">Operatore</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <button
                onClick={createStaff}
                disabled={creating}
                className={`px-6 py-3 rounded-lg font-semibold text-base whitespace-nowrap text-white transition-all duration-200 h-12 ${
                  creating
                    ? "bg-[#C0996F] cursor-not-allowed"
                    : "bg-gradient-to-r from-[#D4A574] to-[#E5923A] hover:from-[#E5923A] hover:to-[#D4A574] hover:shadow-lg active:scale-95"
                }`}
              >
                {creating ? "Creating..." : "Add Staff"}
              </button>
            </div>
          </div>

          {/* Staff List */}
          <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md">
            <h2 className="text-2xl font-bold text-[#1F1411] mb-6">Tenant Staff ({staff.length})</h2>

            {editMessage && (
              <div className="mb-4 p-4 bg-[#D4F1D4] border border-[#90EE90] rounded text-[#1F1411] font-medium text-sm">
                {editMessage}
              </div>
            )}

            {loading && <p className="text-[#8B7355] text-lg">Loading...</p>}
            {error && <p className="text-[#E5923A] font-medium">{error}</p>}

            {!loading && staff.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-[#1F1411] border-collapse">
                  <thead>
                    <tr className="border-b-2 border-[#E8C8B8] bg-[#FAF8F5]">
                      <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Email</th>
                      <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Name</th>
                      <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Role</th>
                      <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Status</th>
                      <th className="text-left py-4 px-4 font-semibold text-[#1F1411]">Created</th>
                      <th className="text-center py-4 px-4 font-semibold text-[#1F1411]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map((s) => (
                      <tr key={s.id} className="border-b border-[#E8C8B8] hover:bg-[#FAF8F5] transition-colors">
                        {editingId === s.id ? (
                          <>
                            <td className="py-4 px-4">
                              <input
                                type="email"
                                value={editData.email}
                                onChange={(e) =>
                                  setEditData({ ...editData, email: e.target.value })
                                }
                                className="w-full px-2 py-1 border border-[#D4A574] rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                              />
                            </td>
                            <td className="py-4 px-4">
                              <input
                                type="text"
                                value={editData.name}
                                onChange={(e) =>
                                  setEditData({ ...editData, name: e.target.value })
                                }
                                className="w-full px-2 py-1 border border-[#D4A574] rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                              />
                            </td>
                            <td colSpan={3}></td>
                            <td className="py-4 px-4 flex gap-2">
                              <button
                                onClick={() => handleUpdate(s.id)}
                                disabled={updatingId === s.id}
                                className="px-3 py-1 rounded border border-[#D4A574] bg-[#FFF5E6] text-[#D4A574] hover:bg-[#FFEFD5] text-xs font-medium cursor-pointer transition-colors disabled:opacity-50"
                              >
                                {updatingId === s.id ? "..." : "Save"}
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="px-3 py-1 rounded border border-[#8B7355] bg-[#F0ECE8] text-[#8B7355] hover:bg-[#E8C8B8] text-xs font-medium cursor-pointer transition-colors"
                              >
                                Cancel
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="py-4 px-4 font-medium">{s.email}</td>
                            <td className="py-4 px-4 text-[#8B7355]">{s.name || "—"}</td>
                            <td className="py-4 px-4">
                              <span
                                className={`inline-block px-3 py-1.5 rounded-full text-xs font-bold ${
                                  s.role === "admin"
                                    ? "bg-[#FFF5E6] text-[#E5923A]"
                                    : "bg-[#F0ECE8] text-[#D4A574]"
                                }`}
                              >
                                {s.role}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <span
                                className={`inline-block px-3 py-1.5 rounded-full text-xs font-bold ${
                                  s.status === "active"
                                    ? "bg-[#D4A574] text-white"
                                    : s.status === "suspended"
                                    ? "bg-[#FDCC88] text-[#1F1411]"
                                    : "bg-[#E8C8B8] text-[#1F1411]"
                                }`}
                              >
                                {s.status}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              {s.createdAt
                                ? new Date(s.createdAt).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })
                                : 'N/A'}
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => startEdit(s)}
                                  className="px-3 py-1 rounded border border-[#D4A574] bg-[#FFF5E6] text-[#D4A574] hover:bg-[#FFEFD5] text-xs font-medium cursor-pointer transition-colors"
                                >
                                  Edit
                                </button>
                                {s.status === "active" && (
                                  <button
                                    onClick={() => handleSuspend(s.id)}
                                    disabled={updatingId === s.id}
                                    className="px-3 py-1 rounded border border-[#FDCC88] bg-[#FFFAE6] text-[#E5923A] hover:bg-[#FFFAE6] text-xs font-medium cursor-pointer transition-colors disabled:opacity-50"
                                  >
                                    Suspend
                                  </button>
                                )}
                                {s.status === "suspended" && (
                                  <button
                                    onClick={() => handleResume(s.id)}
                                    disabled={updatingId === s.id}
                                    className="px-3 py-1 rounded border border-[#D4A574] bg-[#FFF5E6] text-[#D4A574] hover:bg-[#FFEFD5] text-xs font-medium cursor-pointer transition-colors disabled:opacity-50"
                                  >
                                    Resume
                                  </button>
                                )}
                                <button
                                  onClick={() => deleteStaff(s.id)}
                                  className="px-3 py-1 rounded border border-[#E5923A] bg-[#FFF5E6] text-[#E5923A] hover:bg-[#FFEFD5] text-xs font-medium cursor-pointer transition-colors"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && staff.length === 0 && (
              <p className="text-[#C0996F]">No staff members found. Create one above.</p>
            )}
          </div>
        </>
      )}

      {!selectedTenant && (
        <div className="bg-[#FAF8F5] rounded-xl p-6 border border-[#E8C8B8] text-center">
          <p className="text-[#8B7355]">Select a tenant to view and manage its staff</p>
        </div>
      )}
    </div>
  );
}

// ============ TAB: SETTINGS ============
function SettingsTab() {
  return (
    <div className="space-y-6">
      {/* Email Configuration */}
      <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md hover:shadow-lg transition-shadow">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1F1411] mb-1">Email Configuration</h2>
            <p className="text-[#8B7355] text-base">Manage SMTP and email notification settings</p>
          </div>
          <div className="text-4xl">✉️</div>
        </div>
        <div className="bg-[#FAF8F5] border border-[#E8C8B8] rounded-lg p-6">
          <p className="text-[#1F1411] text-sm mb-3">
            SMTP settings are configured via environment variables in your <code className="bg-[#E8C8B8] px-2 py-1 rounded text-xs">.env.dev</code> file
          </p>
          <p className="text-[#8B7355] text-xs font-mono">SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASSWORD</p>
        </div>
      </div>

      {/* AI Settings */}
      <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md hover:shadow-lg transition-shadow">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1F1411] mb-1">AI Settings</h2>
            <p className="text-[#8B7355] text-base">Configure AI models and language features</p>
          </div>
          <div className="text-4xl">🤖</div>
        </div>
        <div className="bg-[#FAF8F5] border border-[#E8C8B8] rounded-lg p-6">
          <div className="inline-block px-4 py-2 bg-[#D4A574] text-white rounded-full text-sm font-semibold">Coming Soon</div>
          <p className="text-[#1F1411] text-sm mt-3">AI model selection and configuration interface will be available soon</p>
        </div>
      </div>

      {/* Services & Integrations */}
      <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1F1411] mb-1">Services & Integrations</h2>
            <p className="text-[#8B7355] text-base">Manage third-party service connections</p>
          </div>
          <div className="text-4xl">🔗</div>
        </div>
        <div className="bg-[#FAF8F5] border border-[#E8C8B8] rounded-lg p-6">
          <div className="inline-block px-4 py-2 bg-[#D4A574] text-white rounded-full text-sm font-semibold">Coming Soon</div>
          <p className="text-[#1F1411] text-sm mt-3">Service management and integration interface will be available soon</p>
        </div>
      </div>
    </div>
  );
}

// ============ TAB: ACCOUNT ============
function AccountTab({ user }: any) {
  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    surname: user?.surname || "",
    phone: user?.phone || "",
    title: user?.title || "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [passwordMode, setPasswordMode] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const handleProfileChange = (field: string, value: string) => {
    setProfileData({ ...profileData, [field]: value });
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const res = await axios.put("/auth/me/profile", {
        name: profileData.name || undefined,
        surname: profileData.surname || null,
        phone: profileData.phone || null,
        title: profileData.title || null,
      });

      if (res.data.success) {
        setMessage("Profile updated successfully");
        setEditMode(false);

        const updatedUser = res.data.data?.user || res.data.data;
        if (updatedUser) {
          useAuthStore.setState((state) => ({
            ...state,
            user: { ...state.user, ...updatedUser },
          }));
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordLoading(true);
    setPasswordError("");
    setPasswordMessage("");

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New passwords do not match");
      setPasswordLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      setPasswordLoading(false);
      return;
    }

    try {
      const res = await axios.post("/auth/change-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });

      if (res.data.success) {
        setPasswordMessage("Password changed successfully");
        setPasswordMode(false);
        setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setTimeout(() => {
          setPasswordMessage("");
        }, 3000);
      }
    } catch (err: any) {
      setPasswordError(err.response?.data?.error?.message || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Personal Information */}
      <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md hover:shadow-lg transition-shadow">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1F1411] mb-1">Personal Information</h2>
            <p className="text-[#8B7355] text-base">Your account details and profile</p>
          </div>
          <div className="text-4xl">👤</div>
        </div>

        {message && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
            ✓ {message}
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            ✕ {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Email (non-editable) */}
          <div>
            <label className="block text-sm font-semibold text-[#1F1411] mb-2">Email Address</label>
            <div className="px-4 py-3 bg-[#FAF8F5] border border-[#E8C8B8] rounded-lg text-[#1F1411] font-medium">{user?.email}</div>
          </div>

          {/* Role (non-editable) */}
          <div>
            <label className="block text-sm font-semibold text-[#1F1411] mb-2">Role</label>
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-[#D4A574] to-[#E5923A] border border-[#D4A574] rounded-full">
              <span className="text-white font-bold text-sm capitalize">{user?.role}</span>
            </div>
          </div>

          {/* Editable Fields */}
          {!editMode ? (
            <>
              {/* Display Mode */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1F1411] mb-2">First Name</label>
                  <div className="px-4 py-3 bg-[#FAF8F5] border border-[#E8C8B8] rounded-lg text-[#1F1411]">
                    {profileData.name || "—"}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1F1411] mb-2">Surname</label>
                  <div className="px-4 py-3 bg-[#FAF8F5] border border-[#E8C8B8] rounded-lg text-[#1F1411]">
                    {profileData.surname || "—"}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1F1411] mb-2">Phone Number</label>
                  <div className="px-4 py-3 bg-[#FAF8F5] border border-[#E8C8B8] rounded-lg text-[#1F1411]">
                    {profileData.phone || "—"}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1F1411] mb-2">Job Title</label>
                  <div className="px-4 py-3 bg-[#FAF8F5] border border-[#E8C8B8] rounded-lg text-[#1F1411]">
                    {profileData.title || "—"}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setEditMode(true)}
                className="px-6 py-3 rounded-lg font-semibold text-base text-white bg-gradient-to-r from-[#D4A574] to-[#E5923A] hover:from-[#E5923A] hover:to-[#D4A574] hover:shadow-lg transition-all duration-200 active:scale-95"
              >
                ✎ Edit Profile
              </button>
            </>
          ) : (
            <>
              {/* Edit Mode */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1F1411] mb-2">First Name</label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => handleProfileChange("name", e.target.value)}
                    placeholder="Your first name"
                    className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1F1411] mb-2">Surname</label>
                  <input
                    type="text"
                    value={profileData.surname}
                    onChange={(e) => handleProfileChange("surname", e.target.value)}
                    placeholder="Your surname"
                    className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1F1411] mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleProfileChange("phone", e.target.value)}
                    placeholder="+39 123 456 7890"
                    className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1F1411] mb-2">Job Title</label>
                  <input
                    type="text"
                    value={profileData.title}
                    onChange={(e) => handleProfileChange("title", e.target.value)}
                    placeholder="e.g. Administrator"
                    className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSaveProfile}
                  disabled={loading}
                  className={`px-6 py-3 rounded-lg font-semibold text-base whitespace-nowrap text-white transition-all duration-200 ${
                    loading
                      ? "bg-[#C0996F] cursor-not-allowed"
                      : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:shadow-lg active:scale-95"
                  }`}
                >
                  {loading ? "Saving..." : "✓ Save Changes"}
                </button>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setProfileData({
                      name: user?.name || "",
                      surname: user?.surname || "",
                      phone: user?.phone || "",
                      title: user?.title || "",
                    });
                  }}
                  disabled={loading}
                  className="px-6 py-3 rounded-lg font-semibold text-base whitespace-nowrap text-[#1F1411] bg-[#E8C8B8] hover:bg-[#D4A574] hover:text-white transition-all duration-200"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-white rounded-xl p-8 border border-[#E8C8B8] shadow-md">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1F1411] mb-1">Security Settings</h2>
            <p className="text-[#8B7355] text-base">Manage your password and authentication</p>
          </div>
          <div className="text-4xl">🔐</div>
        </div>

        {passwordMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
            ✓ {passwordMessage}
          </div>
        )}
        {passwordError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            ✕ {passwordError}
          </div>
        )}

        {!passwordMode ? (
          <button
            onClick={() => setPasswordMode(true)}
            className="px-6 py-3 rounded-lg font-semibold text-base text-white bg-gradient-to-r from-[#D4A574] to-[#E5923A] hover:from-[#E5923A] hover:to-[#D4A574] hover:shadow-lg transition-all duration-200 active:scale-95"
          >
            🔑 Change Password
          </button>
        ) : (
          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-semibold text-[#1F1411] mb-2">Current Password</label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                placeholder="Enter your current password"
                className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1F1411] mb-2">New Password</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="Enter new password (min. 8 characters)"
                className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1F1411] mb-2">Confirm New Password</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                className="w-full px-4 py-3 rounded-lg border border-[#E8C8B8] text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all hover:border-[#D4A574]"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleChangePassword}
                disabled={passwordLoading}
                className={`px-6 py-3 rounded-lg font-semibold text-base whitespace-nowrap text-white transition-all duration-200 ${
                  passwordLoading
                    ? "bg-[#C0996F] cursor-not-allowed"
                    : "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 hover:shadow-lg active:scale-95"
                }`}
              >
                {passwordLoading ? "Changing..." : "✓ Change Password"}
              </button>
              <button
                onClick={() => {
                  setPasswordMode(false);
                  setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
                  setPasswordError("");
                }}
                disabled={passwordLoading}
                className="px-6 py-3 rounded-lg font-semibold text-base whitespace-nowrap text-[#1F1411] bg-[#E8C8B8] hover:bg-[#D4A574] hover:text-white transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
